import { Link } from "@inertiajs/react"
import type { ReactNode } from "react"
import { Card } from "@/components/ui/Card"
import { formatMoney } from "@/lib/money"
import { cn } from "@/lib/utils"
import Delta from "./Delta"
import StatTile from "./StatTile"
import TargetBar from "./TargetBar"
import { type ExcessCounts, excessBreakdown } from "./types"

interface Props {
  currency: string
  /** Printed after the revenue labels on the all-markets Dashboard: "(USD)". */
  currencyInLabel?: boolean
  /** Null = blank (all markets: a market has no exchange rate). */
  revenueThisYear: number | null
  revenueThisMonth: number | null
  changePct: number | null
  plan: {
    target: number | null
    revenue: number | null
    /** The plan window, printed after the target. */
    period?: string
    /** Markets left out of an all-markets total because their plan has no target. */
    notCounted?: string[]
    emptyText?: string
  }
  needBuying: number
  needBuyingHref: string
  excess: ExcessCounts
  /** The Push review is per market, so the all-markets Dashboard has no link. */
  excessHref?: string
}

const blank = <span className="text-muted-foreground">–</span>

function OpenLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link href={href} className="font-medium text-link underline-offset-4 hover:underline">
      {children}
    </Link>
  )
}

/** The same four tiles on the all-markets Dashboard and on each market's page. */
export default function HeadlineTiles({
  currency, currencyInLabel = false, revenueThisYear, revenueThisMonth, changePct, plan, needBuying, needBuyingHref, excess, excessHref,
}: Props) {
  const money = (v: number | null) => (v == null ? blank : formatMoney(v, currency))
  const suffix = currencyInLabel ? ` (${currency})` : ""
  const hasTarget = plan.target != null && plan.target > 0 && plan.revenue != null
  const pct = hasTarget ? Math.round((plan.revenue! / plan.target!) * 100) : null
  const notCounted = plan.notCounted ?? []

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Card className="grid grid-cols-2 divide-x py-4">
        <div className="flex flex-col gap-1 px-5">
          <div className="text-xs font-medium">Need buying</div>
          <div className={cn("text-2xl font-semibold tabular-nums tracking-tight", needBuying > 0 && "text-warning")}>{needBuying}</div>
          <div className="text-xs">Critical or Order soon</div>
          <div className="text-xs"><OpenLink href={needBuyingHref}>Open Inventory →</OpenLink></div>
        </div>
        <div className="flex flex-col gap-1 px-5">
          <div className="text-xs font-medium">Need pushing</div>
          <div className="text-2xl font-semibold tabular-nums tracking-tight">{excess.total}</div>
          <div className="text-xs">{excessBreakdown(excess)}</div>
          {excessHref && <div className="text-xs"><OpenLink href={excessHref}>Open Push Review →</OpenLink></div>}
        </div>
      </Card>
      <StatTile label="Plan progress" value={pct == null ? blank : `${pct}%`}>
        {hasTarget ? (
          <div className="flex flex-col gap-1">
            <TargetBar actual={plan.revenue!} target={plan.target} currency={currency} period={plan.period} showPct={false} />
            {notCounted.length > 0 && <div>Not counted: {notCounted.join(", ")} (no plan target)</div>}
          </div>
        ) : (
          <span className="text-muted-foreground">{plan.emptyText ?? "No plan target to compare with"}</span>
        )}
      </StatTile>
      <StatTile label={`Revenue this month${suffix}`} value={money(revenueThisMonth)}>
        <Delta pct={changePct} /> vs same days last month
      </StatTile>
      <StatTile label={`Revenue this year${suffix}`} value={money(revenueThisYear)}>
        January to today
      </StatTile>
    </div>
  )
}
